import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { TextFieldPropValue } from 'components/FieldNumberFormat';
import { ModalDialog } from 'components/ModalDialog';
import type { WorkOrderChangeObjectIdInput, WorkOrderChangeObjectItem, WorkOrderObjectData } from 'gql/graphql';
import { RatioTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Button, cnMixSpace, Combobox, Steps } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CreateOrEditRatio } from '../Ratio/CreateOrEditRatio/CreateOrEditRatio';
import { groupWorkOrderObjectsByParentId } from '../WorkOrder/AddWorkOrderUer/helpersAndTypes';

import { GridPickCoefficients } from './GridPickCoefficients/GridPickCoefficients';
import { GridSelectingObject } from './GridSelectingObject/GridSelectingObject';
import { useApplyRatioToWorkOrder } from './hooks/useApplyRatioToWorkOrder';
import { useApplyRatioToWorkOrderChange } from './hooks/useApplyRatioToWorkOrderChange';

import Style from './WorkOrderUseCoefficient.module.css';

interface IWorkOrderUseCoefficient {
    closeHandler: () => void;
    workOrderObjects?: (WorkOrderObjectData | WorkOrderChangeObjectItem)[] | null;
    constructionTypeId: number;
    workOrderChangeId?: number;
    updaterApplyRatio?: () => void;
    objectId?: number;
}

type Item = {
    label: string;
    id: number;
};

export interface IVariablesData {
    workOrderObjectIds: number[] | number;
    isRootItem: boolean;
}

export interface IVariablesChangeData {
    workOrderChangeId?: number;
    constructionTypeId: number;
    rootObjectIds?: WorkOrderChangeObjectIdInput;
    objectIds?: WorkOrderChangeObjectIdInput[];
    isRootItem: boolean;
}

const itemsSteps: string[] = ['Выбор объекта', 'Выбор коэффициентов'];
const getLabel = (item: string) => item;
const defaultTabId = RatioTypeEnum.ConstraintCoef;

export const WorkOrderUseCoefficient: React.FC<IWorkOrderUseCoefficient> = (props) => {
    const { closeHandler, constructionTypeId, workOrderChangeId, workOrderObjects, updaterApplyRatio, objectId } =
        props;
    const [isCoefficientCreating, setIsCoefficientCreating] = useState(false);
    const [activeTabId, setActiveTabId] = useState<RatioTypeEnum>(defaultTabId);
    const [activeStep, setActiveStep] = useState<string>(itemsSteps[0]);
    const [valueCombobox, setComboboxValue] = useState<Item | null>();
    const [checkedObjects, setCheckedObjects] = useState<{ [key: number]: boolean | 'intermediate' }>({});
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [valueNote, setValueNote] = useState<Record<string, string>>({});

    const handleChangesValueNote = useCallback(
        (rowId: number) =>
            ({ value }: { value: TextFieldPropValue }) => {
                setValueNote((ids) => ({ ...ids, [rowId]: value }));
            },
        [],
    );

    const gridRef = useRef<AgGridReact>(null);
    const gridPickRef = useRef<AgGridReact>(null);

    const getItemsCombobox = useCallback(
        (data) =>
            data
                ?.filter(
                    ({ constructionType, parentId }: { constructionType: { id: number }; parentId: number }) =>
                        parentId === null && constructionTypeId === constructionType.id,
                )
                .map(({ id, name }: { id: number; name: string }) => ({ id, label: name })) || [],
        [constructionTypeId],
    );

    const comboboxItems = useMemo(() => getItemsCombobox(workOrderObjects), [getItemsCombobox, workOrderObjects]);

    const defaultComboboxValue = useMemo(
        () => comboboxItems.find((item: { id: number }) => item.id === objectId),
        [comboboxItems, objectId],
    );

    useEffect(() => {
        setComboboxValue(defaultComboboxValue);
    }, [defaultComboboxValue]);

    const onCoefficientTypeChange = useCallback((coefficientType) => {
        setActiveTabId(coefficientType);
    }, []);

    const onCreateCoefficientVisibilityChange = useCallback(() => {
        setIsCoefficientCreating(!isCoefficientCreating);
    }, [isCoefficientCreating]);

    const activeStepIndex = itemsSteps.findIndex((item) => item === activeStep);

    const handleNext = () => setActiveStep(itemsSteps[activeStepIndex + 1]);
    const handlePrev = () => setActiveStep(itemsSteps[activeStepIndex - 1]);

    const onChangeLocal = useCallback(
        (valueLocal) => {
            setComboboxValue(valueLocal.value);
            setCheckedObjects({});
        },
        [setComboboxValue],
    );

    const onCloseHandler = useCallback(() => {
        closeHandler();
        setActiveStep(itemsSteps[0]);
    }, [closeHandler]);

    const onChangeSteps = useCallback((values) => {
        setActiveStep(values.value);
    }, []);

    const objGroups = useMemo(() => groupWorkOrderObjectsByParentId(workOrderObjects ?? []), [workOrderObjects]);

    //  для установки состояния всех дочерних объектов
    const setChildrenState = useCallback(
        (parentId: number, state: boolean) => {
            const children = objGroups[parentId];

            if (children) {
                children.forEach((child) => {
                    setCheckedObjects((prev) => ({ ...prev, [child.id]: state }));
                    setChildrenState(child.id, state);
                });
            }
        },
        [objGroups],
    );

    //  для установки состояния родительских объектов
    const updateParentState = useCallback(
        (childId: number) => {
            const child = workOrderObjects?.find((obj) => obj.id === childId);

            if (child?.parentId !== null) {
                const siblings = child?.parentId ? objGroups[child.parentId] : [];

                setCheckedObjects((prev) => {
                    const allChecked = siblings.every((sibling) => prev[sibling.id] === true);
                    const someChecked = siblings.some((sibling) => prev[sibling.id]);

                    if (child?.parentId) {
                        let state: boolean | 'intermediate' = false;

                        if (allChecked) {
                            state = true;
                        } else if (someChecked) {
                            state = 'intermediate';
                        }

                        return {
                            ...prev,
                            [child.parentId]: state,
                        };
                    }

                    return prev;
                });

                updateParentState(child?.parentId);
            }
        },
        [workOrderObjects, objGroups],
    );

    const onChangeCheckedObjects = useCallback(
        (value: { [key: number]: boolean }) => {
            const [currentKey, currentValue] = Object.entries(value)[0];
            const key = Number(currentKey);

            setCheckedObjects((prev) => ({ ...prev, [key]: currentValue }));
            setChildrenState(key, currentValue);
            updateParentState(key);
        },
        [setChildrenState, updateParentState],
    );

    const isNextStepDisabled = useMemo(
        () => !Object.values(checkedObjects ?? {})?.some((checked) => checked),
        [checkedObjects],
    );

    const variablesData = useMemo(() => {
        if (gridRef?.current?.props) {
            const { rowData } = gridRef.current.props;

            const rootItem = rowData?.find((item) => checkedObjects[item.id] === true && item.level === 0);

            const baseItems = rowData?.filter((item) => checkedObjects[item.id] === true)?.map((item) => item.id);

            if (rootItem) {
                return { workOrderObjectIds: rootItem?.id, isRootItem: true };
            }

            return { workOrderObjectIds: baseItems, isRootItem: false };
        }

        return undefined;
    }, [checkedObjects]);

    const variablesChangeData = useMemo(() => {
        const { rowData } = gridRef?.current?.props || {};

        const rootItem = rowData?.find((item) => checkedObjects[item.id] === true && item.level === 0);
        const rootItemChangeObjectsData = workOrderObjects?.find((item) => item?.id === rootItem?.id);

        const objectIds = rowData
            ?.filter((item) => checkedObjects[item.id] === true)
            ?.map(({ workOrderChangeObjectId, workOrderObjectId }) => ({
                workOrderChangeObjectId,
                workOrderObjectId,
            }));

        if (rootItem) {
            return {
                workOrderChangeId,
                constructionTypeId,
                rootObjectIds: {
                    workOrderChangeObjectId: (rootItemChangeObjectsData as WorkOrderChangeObjectItem)
                        ?.workOrderChangeObjectId,
                    workOrderObjectId: (rootItemChangeObjectsData as WorkOrderChangeObjectItem)?.workOrderObjectId,
                },
                isRootItem: true,
            };
        }

        return {
            workOrderChangeId,
            constructionTypeId,
            objectIds: objectIds ?? [],
            isRootItem: false,
        };
    }, [checkedObjects, constructionTypeId, workOrderChangeId, workOrderObjects]);

    const { addRatioToWorkOrderHandler, isLoadingApply } = useApplyRatioToWorkOrder({
        checkedIds,
        variablesData,
        closeHandler,
        updaterApplyRatio,
    });

    const { addRatioToWorkOrderChangeHandler, isLoadingApplyOrderChange } = useApplyRatioToWorkOrderChange({
        checkedIds,
        valueNote,
        variablesChangeData,
        closeHandler,
        updaterApplyRatio,
    });

    const onConfirmCreateRatio = useCallback(() => {
        if (gridPickRef.current) {
            gridPickRef.current?.api?.refreshInfiniteCache?.();
        }
        setIsCoefficientCreating(!isCoefficientCreating);
    }, [isCoefficientCreating]);

    const hasCheckedItems = useMemo(() => !Object.values(checkedIds).some((value) => value === true), [checkedIds]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onCloseHandler,
        });

    const oncloseModal = useCallback(() => {
        if (!isNextStepDisabled) {
            handleOpenConfirmationDialog();
        } else {
            onCloseHandler();
        }
    }, [handleOpenConfirmationDialog, isNextStepDisabled, onCloseHandler]);

    const addCurrentRatioHandler = useCallback(() => {
        if (workOrderChangeId) {
            addRatioToWorkOrderChangeHandler();
        } else {
            addRatioToWorkOrderHandler();
        }
    }, [addRatioToWorkOrderChangeHandler, addRatioToWorkOrderHandler, workOrderChangeId]);

    const isLoading = useMemo(
        () => (workOrderChangeId ? isLoadingApplyOrderChange : isLoadingApply),
        [isLoadingApply, isLoadingApplyOrderChange, workOrderChangeId],
    );

    return (
        <ModalDialog
            isOpen
            title="Применение коэффициента стесненности к объекту"
            hasOverlay
            onClose={oncloseModal}
            isCommit={isLoading}
            withTitleCloseButton
            className={Style.Modal}
            isOnClickOutsideDisable
            withBottomButtons={false}
            style={
                {
                    '--modal_width': activeStepIndex === 0 ? '600px' : '1344px',
                } as React.CSSProperties
            }
        >
            <div className={Style.ContentModal}>
                <div className={Style.wrapperContent}>
                    <Steps
                        className={Style.Steps}
                        getItemDisabled={(item) => item === itemsSteps[1] && isNextStepDisabled}
                        getItemCompleted={(item) => item === itemsSteps[0] && !isNextStepDisabled}
                        items={itemsSteps}
                        getItemLabel={getLabel}
                        value={activeStep}
                        onChange={onChangeSteps}
                        size="s"
                    />
                    <section>
                        {activeStepIndex === 0 && (
                            <>
                                <Combobox
                                    className={Style.Combobox}
                                    placeholder="Объект строительства"
                                    items={comboboxItems}
                                    value={valueCombobox}
                                    onChange={onChangeLocal}
                                    size="s"
                                />
                                <GridSelectingObject
                                    checkedObjects={checkedObjects}
                                    isShowTable={!!valueCombobox}
                                    onChangeCheckedObjects={onChangeCheckedObjects}
                                    currentDataItems={workOrderObjects}
                                    comboxId={valueCombobox?.id}
                                    constructionTypeId={constructionTypeId}
                                    gridRef={gridRef}
                                />
                            </>
                        )}
                        {activeStepIndex === 1 && (
                            <GridPickCoefficients
                                checkedIds={checkedIds}
                                setCheckedIds={setCheckedIds}
                                variablesData={variablesData}
                                variablesChangeData={variablesChangeData}
                                isWorkOrderChange={!!workOrderChangeId}
                                handleChangesValueNote={handleChangesValueNote}
                                valueNote={valueNote}
                                gridRef={gridPickRef}
                            />
                        )}
                    </section>
                </div>
                <div className={Style.footerDialog}>
                    {activeStepIndex === 0 && (
                        <Button label="Далее" onClick={handleNext} disabled={isNextStepDisabled} />
                    )}
                    {activeStepIndex === 1 && (
                        <div className={Style.WrapButton}>
                            <Button
                                view="secondary"
                                className={cnMixSpace({ mR: 'auto' })}
                                label="Создать коэффициент"
                                onClick={onCreateCoefficientVisibilityChange}
                            />
                            <div>
                                <Button
                                    className={cnMixSpace({ mR: 'm' })}
                                    label={workOrderChangeId ? 'Добавить в Изм. к НЗ' : 'Добавить в Наряд-Заказ'}
                                    onClick={addCurrentRatioHandler}
                                    disabled={hasCheckedItems}
                                />
                                <Button view="ghost" label="Назад" onClick={handlePrev} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {isCoefficientCreating && (
                <CreateOrEditRatio
                    ratioType={activeTabId}
                    onClose={onCreateCoefficientVisibilityChange}
                    onConfirm={onConfirmCreateRatio}
                    onRatioTypeChange={onCoefficientTypeChange}
                    setCheckedIds={setCheckedIds}
                />
            )}
            <ConfirmationDialog
                textHeader="Отмена действий"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

WorkOrderUseCoefficient.displayName = 'WorkOrderUseCoefficient';
